import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import React, { useEffect, useState, useRef } from 'react';
import { useIsAuthenticated } from '../hooks/useAuth';
import { SignalRContext } from './signalr-context';

interface SignalRProviderProps {
  children: React.ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useIsAuthenticated();
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
        setConnection(null);
        setIsConnected(false);
      }
      return;
    }

    // Se já existe uma conexão, não criar nova
    if (connectionRef.current) {
      return;
    }

    // SignalR URL - usa URL relativa para funcionar via proxy/tunnel
    const signalRUrl = '/hubs/log';

    const newConnection = new HubConnectionBuilder()
      .withUrl(signalRUrl, {
        transport: HttpTransportType.LongPolling,
        skipNegotiation: false,
        timeout: 60000,
        // Lê o token no momento de cada requisição, evitando closure stale
        accessTokenFactory: () => localStorage.getItem('auth_token') || ''
      })
      .withAutomaticReconnect([0, 2000, 10000])
      .configureLogging(LogLevel.Information) // Info para produção
      .build();

    const startConnection = async () => {
      try {
        await newConnection.start();
        setIsConnected(true);

        newConnection.on('ReceberLog', (_message: string) => {
          // handler para logs do catalogo
        });

        newConnection.on('TestMessage', (_message: string) => {});
        newConnection.on('TestMessageReceived', (_message: string) => {});

      } catch {
        setIsConnected(false);
      }
    };

    newConnection.onclose(() => setIsConnected(false));
    newConnection.onreconnected(() => setIsConnected(true));
    newConnection.onreconnecting(() => setIsConnected(false));

    connectionRef.current = newConnection;
    setConnection(newConnection);
    startConnection();

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [isAuthenticated]); // Usando connectionRef para evitar loop de dependências

  const value = {
    connection,
    isConnected,
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  );
};